import { resolveId } from '@medplum/core'
import type { Participant } from '@seen/core/models/patients/participant'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { getReferenceString } from '@seen/core/references'
import { DotSeparator } from '@seen/ui'
import { type JSX, useContext, useMemo } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { IconUserPlaceholder } from '#src/components/icons/IconUserPlaceholder.tsx'
import { PatientName } from '#src/components/patient/PatientName.tsx'
import { extractMentionReferences } from '#src/components/rich-text-editor/mention/extractMentionReferences.ts'
import { CurrentParticipantContext } from '#src/contexts/current-participant/CurrentParticipantContext.tsx'
import { extractTitleFromHTML } from '#src/utils/html.ts'

export function TaskMentionContent({
  task,
  participant,
  display,
}: {
  task: StandardTask | undefined
  participant: Participant | undefined
  display: string | undefined
}): JSX.Element {
  const participantId = resolveId(task?.participant)
  const rawTitle = task?.title ?? display ?? ''
  const title = extractTitleFromHTML(rawTitle)
  const currentParticipant = useContext(CurrentParticipantContext)

  const isParticipantMentionedInTitle = useMemo(() => {
    if (!task?.participant) {
      return false
    }
    const participantRefString = getReferenceString(task.participant)
    const mentionRefs = extractMentionReferences(rawTitle)
    return mentionRefs.some(
      (ref) => getReferenceString(ref) === participantRefString
    )
  }, [rawTitle, task?.participant])

  return (
    <>
      <span className="whitespace-nowrap">
        <span className="relative top-0.75 -mt-1 inline-flex items-center">
          {task?.state && <IconTaskState state={task.state} />}
          <span className="relative -ml-1 inline-flex">
            <span className="inline-flex">
              {(!task || !!task?.ownerPractitioner) && (
                <span className="inline-flex rounded-full border border-gray-470 bg-gray-470">
                  <Avatar profile={task?.ownerPractitioner} size={3.5} />
                </span>
              )}
              {!!task && !task?.ownerPractitioner && (
                <span className="inline-flex rounded-full bg-gray-470">
                  <IconUserPlaceholder className="size-4 p-0" />
                </span>
              )}
            </span>
          </span>
        </span>{' '}
        {currentParticipant?.id !== participantId &&
          participant &&
          !isParticipantMentionedInTitle && (
            <span className="text-foreground-secondary">
              <DotSeparator />
              <PatientName participant={participant} />
              <DotSeparator />
            </span>
          )}
      </span>{' '}
      <span>{title}</span>
    </>
  )
}
